import { debounce } from './theme.js';

export class CollectionInfo extends HTMLElement {
  constructor() {
    super();
    this.debounceOnChange = debounce((event) => this.onChangeHandler(event), 800);
    this.addEventListener('change', this.debounceOnChange.bind(this));
    this.addEventListener('click', this.onClickHandler.bind(this));
  }

  onClickHandler = (event) => {
    if (event.target.matches('[data-render-section-url]')) {
      event.preventDefault();
      const searchParams = new URLSearchParams(event.target.dataset.renderSectionUrl.split('?')[1] || '');
      const existingParams = new URLSearchParams(window.location.search);
      const qValue = existingParams.get('q');
      const typeValue = existingParams.get('type');
      if (qValue) searchParams.set('q', qValue);
      if (typeValue) searchParams.set('type', typeValue);
      this.fetchSection(searchParams.toString());
    }
  };

  onChangeHandler = (event) => {
    if (!event.target.matches('[data-render-section]')) return;

    // Mobile sort pill — update sort_by on current URL params (not form-bound)
    if (event.target.matches('[data-collection-sort]')) {
      const valueEl = event.target.closest('label')?.querySelector('[data-sort-pill-value]');
      if (valueEl) {
        valueEl.textContent = event.target.options[event.target.selectedIndex]?.textContent?.trim() || '';
      }
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('sort_by', event.target.value);
      this.fetchSection(searchParams.toString());
      return;
    }

    const form = event.target.closest('form') || document.querySelector('#filters-form') || document.querySelector('#filters-form-drawer');
    if (!form) return;

    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);
    const existingParams = new URLSearchParams(window.location.search);
    const qValue = existingParams.get('q');
    const typeValue = existingParams.get('type');

    this.removeDefaultPriceFilters(searchParams, form);

    if (qValue) searchParams.set('q', qValue);
    if (typeValue) searchParams.set('type', typeValue);

    this.fetchSection(searchParams.toString());
  };

  get form() {
    return this.querySelector('collection-info form');
  }

  updateSourceFromDestination = (html, id) => {
    const source = html.getElementById(`${id}`);
    const destination = this.querySelector(`#${id}`);
    if (source && destination) {
      destination.innerHTML = source.innerHTML;
    }
  };

  removeDefaultPriceFilters = (searchParams, form) => {
    const priceMin = searchParams.get('filter.v.price.gte');
    const priceMax = searchParams.get('filter.v.price.lte');
    const actualMaxPrice = form.querySelector('.max-range')?.getAttribute('max');

    const isMinAtDefault = priceMin === '0' || !priceMin;
    const isMaxAtDefault = !actualMaxPrice || priceMax === actualMaxPrice || !priceMax;

    if (isMinAtDefault && isMaxAtDefault) {
      searchParams.delete('filter.v.price.gte');
      searchParams.delete('filter.v.price.lte');
    }
  };

  updateURL(searchParams) {
    history.pushState({}, '', `${window.location.pathname}?${searchParams}`);
  }

  updateFilters = (html, className) => {
    const filtersFromFetch = html.querySelectorAll(`collection-info .${className}`);
    const filtersFromDom = document.querySelectorAll(`collection-info .${className}`);

    // Remove filters that are no longer returned from the server
    Array.from(filtersFromDom).forEach((currentElement) => {
      if (!Array.from(filtersFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    Array.from(filtersFromFetch).forEach((elementToRender, index) => {
      document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
    });
  };

  showLoadingOverlay = () => {
    const loadingOverlay = this.querySelector(`#loading-overlay-${this.dataset.section}`);
    const loadingSpinner = this.querySelector(`#loading-spinner-${this.dataset.section}`);
    const resultsCount = this.querySelector(`#results-count-${this.dataset.section}`);
    const drawerResultsCount = this.querySelector(`#drawer-results-count-${this.dataset.section}`);
    const drawerLoadingSpinner = this.querySelector(`#drawer-loading-spinner-${this.dataset.section}`);

    if (loadingOverlay) loadingOverlay.style.display = 'flex';
    if (loadingSpinner) loadingSpinner.style.display = 'block';
    if (resultsCount) resultsCount.style.display = 'none';
    if (drawerResultsCount) drawerResultsCount.style.display = 'none';
    if (drawerLoadingSpinner) drawerLoadingSpinner.style.display = 'block';
  };

  hideLoadingOverlay = () => {
    const loadingOverlay = this.querySelector(`#loading-overlay-${this.dataset.section}`);
    const loadingSpinner = this.querySelector(`#loading-spinner-${this.dataset.section}`);
    const resultsCount = this.querySelector(`#results-count-${this.dataset.section}`);
    const drawerResultsCount = this.querySelector(`#drawer-results-count-${this.dataset.section}`);
    const drawerLoadingSpinner = this.querySelector(`#drawer-loading-spinner-${this.dataset.section}`);

    if (loadingOverlay) loadingOverlay.style.display = 'none';
    if (loadingSpinner) loadingSpinner.style.display = 'none';
    if (resultsCount) resultsCount.style.display = 'block';
    if (drawerResultsCount) drawerResultsCount.style.display = 'block';
    if (drawerLoadingSpinner) drawerLoadingSpinner.style.display = 'none';
  };

  fetchSection = (searchParams) => {
    this.showLoadingOverlay();
    fetch(`${window.location.pathname}?section_id=${this.dataset.section}&${searchParams}`)
      .then((response) => response.text())
      .then((responseText) => {
        let html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateURL(searchParams);
        this.updateSourceFromDestination(html, `product-grid-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `results-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `drawer-results-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `active-filters-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `active-filter-group-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `sort-by-drawer-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `sort-by-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `filters-drawer-buttons-wrapper-id`);
        this.updateFilters(html, `js-filter`);
        this.hideLoadingOverlay();
        this.scrollToProductGrid();
      })
      .catch((error) => {
        console.error(error);
        this.hideLoadingOverlay();
      });
  };

  scrollToProductGrid = () => {
    const productGrid = this.querySelector(`#product-grid-${this.dataset.section}`);
    const headerId = document.querySelector('#main-header');
    const headerOffset = (headerId.offsetHeight + 10) || 80;

    if (productGrid) {
      const elementPosition = productGrid.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
}

if (!customElements.get('collection-info')) {
  customElements.define('collection-info', CollectionInfo);
}

class CollectionDescription extends HTMLElement {
  connectedCallback() {
    this.textEl = this.querySelector('[data-description-text]');
    this.toggleEl = this.querySelector('[data-description-toggle]');
    this.toggleLabelEl = this.querySelector('[data-description-toggle-label]');
    if (!this.textEl || !this.toggleEl) return;

    this.toggleEl.addEventListener('click', this.onToggle);
    this.resizeObserver = new ResizeObserver(this.checkOverflow);
    this.resizeObserver.observe(this.textEl);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  checkOverflow = () => {
    if (this.classList.contains('is-expanded')) return;
    const isOverflowing = this.textEl.scrollHeight > this.textEl.clientHeight + 1;
    this.toggleEl.hidden = !isOverflowing;
    this.classList.toggle('is-clamped', isOverflowing);
  };

  onToggle = () => {
    const isExpanded = this.classList.toggle('is-expanded');
    this.toggleEl.setAttribute('aria-expanded', String(isExpanded));

    if (this.toggleLabelEl) {
      this.toggleLabelEl.textContent = isExpanded ? this.toggleEl.dataset.labelLess : this.toggleEl.dataset.labelMore;
    }
  };
}

if (!customElements.get('collection-description')) {
  customElements.define('collection-description', CollectionDescription);
}
